import { useEffect, useMemo, useState } from "react";
import { BookOpen, Copy, Dumbbell, Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useTranslation } from "@/hooks/useTranslation";
import { ExerciseManagerDialog } from "./components/ExerciseManagerDialog";
import { StrengthExerciseOverview } from "./components/StrengthExerciseOverview";
import { StrengthHistoryTable } from "./components/StrengthHistoryTable";
import { StrengthProgressChart } from "./components/StrengthProgressChart";
import { StrengthSessionDialog } from "./components/StrengthSessionDialog";
import { CopyTrainingDialog } from "./components/CopyTrainingDialog";
import {
  ApplyTrainingTemplateDialog,
  SaveTrainingTemplateDialog,
} from "./components/TrainingTemplateDialogs";
import type {
  StrengthClient,
  StrengthExercise,
  StrengthSession,
  StrengthSessionFormData,
} from "./types";

function getClientDisplayName(client: StrengthClient) {
  const fullName = [client.meno, client.priezvisko].filter(Boolean).join(" ");
  return fullName ? `${client.alias} (${fullName})` : client.alias;
}

export default function StrengthProgressPage() {
  const { t, i18n } = useTranslation();
  const {
    canManageExercises,
    canReadOwnStrengthProgress,
    canWriteStrengthProgress,
  } = usePermissions();
  const utils = trpc.useUtils();
  const [selectedKlientId, setSelectedKlientId] = useState<number | null>(null);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(
    null
  );
  const [isSessionDialogOpen, setIsSessionDialogOpen] = useState(false);
  const [isExerciseDialogOpen, setIsExerciseDialogOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [templateSession, setTemplateSession] =
    useState<StrengthSession | null>(null);
  const [editingSession, setEditingSession] = useState<StrengthSession | null>(
    null
  );
  const [selectedTimedGroupIds, setSelectedTimedGroupIds] = useState<
    Set<number>
  >(new Set());

  const { data: clients, isLoading: isClientsLoading } =
    trpc.klient.list.useQuery(undefined, {
      enabled: canReadOwnStrengthProgress,
    });
  const trainingClients = useMemo(
    () =>
      ((clients ?? []) as StrengthClient[]).filter(
        client => client.odberTrening
      ),
    [clients]
  );

  useEffect(() => {
    if (
      trainingClients.length === 1 &&
      selectedKlientId !== trainingClients[0].id
    ) {
      setSelectedKlientId(trainingClients[0].id);
    }
  }, [selectedKlientId, trainingClients]);

  const { data: exercises, isLoading: isExercisesLoading } =
    trpc.cvik.list.useQuery(
      {
        klientId: selectedKlientId!,
        includeInactive: canManageExercises,
      },
      { enabled: selectedKlientId != null && canReadOwnStrengthProgress }
    );
  const exerciseList = (exercises ?? []) as StrengthExercise[];
  const activeExercises = useMemo(
    () => exerciseList.filter(exercise => exercise.aktivny),
    [exerciseList]
  );

  const { data: sessions, isLoading: isSessionsLoading } =
    trpc.silovyTrening.list.useQuery(
      { klientId: selectedKlientId!, limit: 100 },
      { enabled: selectedKlientId != null && canReadOwnStrengthProgress }
    );
  const sessionList = (sessions ?? []) as StrengthSession[];

  const timedGroupIds = useMemo(
    () =>
      sessionList.flatMap(session =>
        (session.timedGroups ?? [])
          .filter(
            group =>
              selectedExerciseId === null || group.cvikId === selectedExerciseId
          )
          .map(group => group.id)
      ),
    [selectedExerciseId, sessionList]
  );
  useEffect(() => {
    setSelectedTimedGroupIds(new Set());
  }, [selectedKlientId, selectedExerciseId]);

  useEffect(() => {
    const availableIds = new Set(timedGroupIds);
    setSelectedTimedGroupIds(current => {
      const next = new Set(
        Array.from(current).filter(groupId => availableIds.has(groupId))
      );
      return next.size === current.size ? current : next;
    });
  }, [timedGroupIds]);

  const selectedTimedGroupIdList = useMemo(
    () =>
      Array.from(selectedTimedGroupIds)
        .filter(id => timedGroupIds.includes(id))
        .sort((left, right) => left - right),
    [selectedTimedGroupIds, timedGroupIds]
  );
  const isComparisonMode = selectedTimedGroupIdList.length > 0;
  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
  } = trpc.silovyTrening.overview.useQuery(
    { klientId: selectedKlientId!, cvikId: selectedExerciseId ?? undefined },
    {
      enabled:
        selectedKlientId !== null &&
        canReadOwnStrengthProgress &&
        !isComparisonMode,
    }
  );
  const { data: timedProgress, isLoading: isTimedProgressLoading } =
    trpc.silovyTrening.timedProgress.useQuery(
      {
        klientId: selectedKlientId!,
        cvikId: selectedExerciseId ?? undefined,
        groupIds: selectedTimedGroupIdList,
      },
      {
        enabled:
          selectedKlientId != null &&
          isComparisonMode &&
          canReadOwnStrengthProgress,
      }
    );
  const timedProgressPoints = timedProgress ?? [];

  const exerciseFilterOptions = useMemo(() => {
    const options = new Map<
      number,
      { id: number; nazov: string; aktivny: boolean }
    >(exerciseList.map(exercise => [exercise.id, exercise]));
    const recordedExercises = [
      ...sessionList.flatMap(session => session.serie),
      ...(overview ?? []),
    ];
    for (const exercise of recordedExercises) {
      if (!options.has(exercise.cvikId)) {
        options.set(exercise.cvikId, {
          id: exercise.cvikId,
          nazov: exercise.cvikNazov,
          aktivny: false,
        });
      }
    }
    return Array.from(options.values());
  }, [exerciseList, sessionList, overview]);

  useEffect(() => {
    if (
      selectedExerciseId !== null &&
      !isExercisesLoading &&
      !isSessionsLoading &&
      !isOverviewLoading &&
      !exerciseFilterOptions.some(
        exercise => exercise.id === selectedExerciseId
      )
    ) {
      setSelectedExerciseId(null);
    }
  }, [
    exerciseFilterOptions,
    isExercisesLoading,
    isSessionsLoading,
    isOverviewLoading,
    selectedExerciseId,
  ]);

  const createSession = trpc.silovyTrening.create.useMutation();
  const updateSession = trpc.silovyTrening.update.useMutation();
  const deleteSession = trpc.silovyTrening.delete.useMutation();
  const createExercise = trpc.cvik.create.useMutation();
  const updateExercise = trpc.cvik.update.useMutation();
  const isSaving =
    createSession.isPending ||
    updateSession.isPending ||
    deleteSession.isPending ||
    createExercise.isPending ||
    updateExercise.isPending;

  const invalidateStrengthData = async () => {
    await Promise.all([
      utils.cvik.list.invalidate(),
      utils.silovyTrening.list.invalidate(),
      utils.silovyTrening.progress.invalidate(),
      utils.silovyTrening.overview.invalidate(),
      utils.silovyTrening.timedProgress.invalidate(),
    ]);
  };

  const handleSaveSession = async (data: StrengthSessionFormData) => {
    if (!selectedKlientId) return;
    try {
      if (editingSession) {
        await updateSession.mutateAsync({ id: editingSession.id, data });
        toast.success(t("strengthProgress:messages.sessionUpdated"));
      } else {
        await createSession.mutateAsync({
          klientId: selectedKlientId,
          ...data,
        });
        toast.success(t("strengthProgress:messages.sessionCreated"));
      }
      await invalidateStrengthData();
      setIsSessionDialogOpen(false);
      setEditingSession(null);
    } catch {
      toast.error(t("strengthProgress:messages.saveFailed"));
    }
  };

  const handleDeleteSession = async (session: StrengthSession) => {
    if (!confirm(t("strengthProgress:messages.deleteConfirm"))) return;
    try {
      await deleteSession.mutateAsync({ id: session.id });
      await invalidateStrengthData();
      toast.success(t("strengthProgress:messages.sessionDeleted"));
    } catch {
      toast.error(t("strengthProgress:messages.saveFailed"));
    }
  };

  const handleCreateExercise = async (data: {
    nazov: string;
    poradie: number;
  }) => {
    if (!selectedKlientId) return;
    try {
      await createExercise.mutateAsync({ klientId: selectedKlientId, ...data });
      await invalidateStrengthData();
      toast.success(t("strengthProgress:messages.exerciseCreated"));
    } catch {
      toast.error(t("strengthProgress:messages.saveFailed"));
    }
  };

  const handleUpdateExercise = async (
    id: number,
    data: { nazov?: string; poradie?: number; aktivny?: boolean }
  ) => {
    if (!selectedKlientId) return;
    try {
      await updateExercise.mutateAsync({
        klientId: selectedKlientId,
        id,
        data,
      });
      await invalidateStrengthData();
      toast.success(t("strengthProgress:messages.exerciseUpdated"));
    } catch {
      toast.error(t("strengthProgress:messages.saveFailed"));
    }
  };

  const latestTimedPoint = timedProgressPoints.at(-1);
  const hasSelectedExerciseSessions = sessionList.some(session =>
    session.serie.some(
      set => selectedExerciseId === null || set.cvikId === selectedExerciseId
    )
  );
  const formatNumber = (value: number) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const formatPercent = (value: number | undefined) =>
    value === undefined ? "—" : `${formatNumber(value)}%`;

  if (!canReadOwnStrengthProgress) return null;

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {t("strengthProgress:title")}
            </h1>
            <p className="text-muted-foreground">
              {t("strengthProgress:subtitle")}
            </p>
          </div>
          {selectedKlientId && (
            <div className="flex flex-wrap gap-2">
              {canManageExercises && (
                <Button
                  variant="outline"
                  onClick={() => setIsExerciseDialogOpen(true)}
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  {t("strengthProgress:manageExercises")}
                </Button>
              )}
              {canWriteStrengthProgress && (
                <Button variant="outline" onClick={() => setCopyOpen(true)}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t("strengthProgress:copy.title")}
                </Button>
              )}
              {canWriteStrengthProgress && (
                <Button
                  variant="outline"
                  onClick={() => setApplyTemplateOpen(true)}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {t("strengthProgress:templates.apply")}
                </Button>
              )}
              {canWriteStrengthProgress && (
                <Button
                  disabled={activeExercises.length === 0}
                  onClick={() => {
                    setEditingSession(null);
                    setIsSessionDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("strengthProgress:addSession")}
                </Button>
              )}
            </div>
          )}
        </div>

        {isClientsLoading ? (
          <p className="py-12 text-center text-muted-foreground">
            {t("common:loading")}
          </p>
        ) : trainingClients.length === 0 ? (
          <Empty className="min-h-72">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Dumbbell />
              </EmptyMedia>
              <EmptyTitle>
                {t("strengthProgress:empty.noClients.title")}
              </EmptyTitle>
              <EmptyDescription>
                {t("strengthProgress:empty.noClients.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("strengthProgress:filters.client")}</Label>
                <Select
                  value={selectedKlientId?.toString() ?? ""}
                  onValueChange={value => {
                    setSelectedKlientId(Number(value));
                    setSelectedExerciseId(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("strengthProgress:filters.chooseClient")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {trainingClients.map(client => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {getClientDisplayName(client)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedKlientId && (
                <div className="space-y-2">
                  <Label>{t("strengthProgress:filters.exercise")}</Label>
                  <Select
                    value={selectedExerciseId?.toString() ?? "all"}
                    onValueChange={value =>
                      setSelectedExerciseId(
                        value === "all" ? null : Number(value)
                      )
                    }
                    disabled={isExercisesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "strengthProgress:filters.chooseExercise"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("strengthProgress:filters.allExercises")}
                      </SelectItem>
                      {exerciseFilterOptions.map(exercise => (
                        <SelectItem
                          key={exercise.id}
                          value={exercise.id.toString()}
                        >
                          {exercise.nazov}
                          {!exercise.aktivny
                            ? ` (${t("strengthProgress:archived")})`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {!selectedKlientId ? (
              <Empty className="min-h-72">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Dumbbell />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t("strengthProgress:empty.chooseClient.title")}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t("strengthProgress:empty.chooseClient.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : isExercisesLoading ||
              isSessionsLoading ||
              (!isComparisonMode && isOverviewLoading) ? (
              <p className="py-12 text-center text-muted-foreground">
                {t("common:loading")}
              </p>
            ) : exerciseFilterOptions.length === 0 && !isOverviewError ? (
              <Empty className="min-h-72">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Settings2 />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t("strengthProgress:empty.noExercises.title")}
                  </EmptyTitle>
                  <EmptyDescription>
                    {t("strengthProgress:empty.noExercises.description")}
                  </EmptyDescription>
                </EmptyHeader>
                {canManageExercises && (
                  <EmptyContent>
                    <Button onClick={() => setIsExerciseDialogOpen(true)}>
                      {t("strengthProgress:manageExercises")}
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            ) : (
              <>
                {isSessionsLoading ||
                (!isComparisonMode && isOverviewLoading) ? (
                  <p className="py-12 text-center text-muted-foreground">
                    {t("common:loading")}
                  </p>
                ) : hasSelectedExerciseSessions ||
                  (!isComparisonMode &&
                    ((overview?.length ?? 0) > 0 || isOverviewError)) ? (
                  <div className="space-y-6">
                    {!isComparisonMode ? (
                      isOverviewError ? (
                        <p role="alert" className="text-sm text-destructive">
                          {t("strengthProgress:messages.loadFailed")}
                        </p>
                      ) : (
                        (overview ?? []).map(exercise => (
                          <StrengthExerciseOverview
                            key={exercise.cvikId}
                            exerciseName={exercise.cvikNazov}
                            points={exercise.points}
                          />
                        ))
                      )
                    ) : isTimedProgressLoading ? (
                      <p className="py-8 text-center text-muted-foreground">
                        {t("common:loading")}
                      </p>
                    ) : timedProgressPoints.length > 0 ? (
                      <>
                        {latestTimedPoint && (
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                  {t("strengthProgress:summary.latestVolume")}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <span className="text-2xl font-bold">
                                  {formatNumber(latestTimedPoint.volumeKg)} kg
                                </span>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {latestTimedPoint.setCount}{" "}
                                  {t("strengthProgress:summary.sets")}
                                </p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                  {t("strengthProgress:summary.volumeChange")}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <span className="text-2xl font-bold">
                                  {formatPercent(
                                    latestTimedPoint.volumeChangePercent
                                  )}
                                </span>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                  {t(
                                    "strengthProgress:summary.latestVolumePerMinute"
                                  )}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <span className="text-2xl font-bold">
                                  {formatNumber(
                                    latestTimedPoint.volumePerMinuteKg
                                  )}{" "}
                                  kg/min
                                </span>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">
                                  {t(
                                    "strengthProgress:summary.volumePerMinuteChange"
                                  )}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <span className="text-2xl font-bold">
                                  {formatPercent(
                                    latestTimedPoint.volumePerMinuteChangePercent
                                  )}
                                </span>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                        <StrengthProgressChart data={timedProgressPoints} />
                      </>
                    ) : (
                      <Card>
                        <CardContent className="py-8 text-center text-sm text-muted-foreground">
                          {t("strengthProgress:chart.emptyComparison")}
                        </CardContent>
                      </Card>
                    )}
                    <StrengthHistoryTable
                      canWrite={canWriteStrengthProgress}
                      selectedExerciseId={selectedExerciseId ?? undefined}
                      selectedTimedGroupIds={selectedTimedGroupIds}
                      sessions={sessionList}
                      onToggleTimedGroup={(groupId, selected) =>
                        setSelectedTimedGroupIds(current => {
                          const next = new Set(current);
                          if (selected) next.add(groupId);
                          else next.delete(groupId);
                          return next;
                        })
                      }
                      onSelectAllTimedGroups={() =>
                        setSelectedTimedGroupIds(new Set(timedGroupIds))
                      }
                      onClearTimedGroups={() =>
                        setSelectedTimedGroupIds(new Set())
                      }
                      onEdit={session => {
                        setEditingSession(session);
                        setIsSessionDialogOpen(true);
                      }}
                      onDelete={handleDeleteSession}
                      onSaveTemplate={setTemplateSession}
                    />
                  </div>
                ) : (
                  <Empty className="min-h-72">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Dumbbell />
                      </EmptyMedia>
                      <EmptyTitle>
                        {t("strengthProgress:empty.noSessions.title")}
                      </EmptyTitle>
                      <EmptyDescription>
                        {t("strengthProgress:empty.noSessions.description")}
                      </EmptyDescription>
                    </EmptyHeader>
                    {canWriteStrengthProgress && (
                      <EmptyContent>
                        <Button onClick={() => setIsSessionDialogOpen(true)}>
                          <Plus className="mr-2 h-4 w-4" />
                          {t("strengthProgress:addSession")}
                        </Button>
                      </EmptyContent>
                    )}
                  </Empty>
                )}
              </>
            )}
          </>
        )}
      </div>

      <StrengthSessionDialog
        defaultExerciseId={selectedExerciseId ?? undefined}
        exercises={exerciseList}
        isSaving={isSaving}
        open={isSessionDialogOpen}
        session={editingSession}
        onOpenChange={open => {
          setIsSessionDialogOpen(open);
          if (!open) setEditingSession(null);
        }}
        onSubmit={handleSaveSession}
      />
      <ExerciseManagerDialog
        exercises={exerciseList}
        isSaving={isSaving}
        open={isExerciseDialogOpen}
        onOpenChange={setIsExerciseDialogOpen}
        onCreate={handleCreateExercise}
        onUpdate={handleUpdateExercise}
      />
      {copyOpen &&
        selectedKlientId &&
        trainingClients.find(client => client.id === selectedKlientId) && (
          <CopyTrainingDialog
            target={
              trainingClients.find(client => client.id === selectedKlientId)!
            }
            clients={(clients ?? []) as StrengthClient[]}
            onClose={() => setCopyOpen(false)}
            onCopied={() => void invalidateStrengthData()}
          />
        )}
      {applyTemplateOpen && selectedKlientId && (
        <ApplyTrainingTemplateDialog
          clientId={selectedKlientId}
          onClose={() => setApplyTemplateOpen(false)}
          onApplied={() => void invalidateStrengthData()}
        />
      )}
      {templateSession && (
        <SaveTrainingTemplateDialog
          session={templateSession}
          onClose={() => setTemplateSession(null)}
        />
      )}
    </DashboardLayout>
  );
}
